import React, { useEffect, useState, useMemo } from 'react';
import {
  Card, Form, Select, InputNumber, DatePicker, Input, Button,
  Table, Space, message, Slider, Typography, Divider,
} from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { quotationApi, customerApi, productApi } from '../../services/api';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const QUOTATION_TYPES = ['产品销售', '项目工程', '运维服务'];

export default function QuotationForm() {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [items, setItems] = useState([{
    key: 1,
    productId: null,
    name: '',
    spec: '',
    quantity: 1,
    unit: '台',
    unitPrice: 0,
    discount: 0,
    subtotal: 0,
  }]);

  useEffect(() => {
    customerApi.list({ pageSize: 200 }).then((res) => setCustomers(res.data)).catch(() => {});
    productApi.list({ pageSize: 500 }).then((res) => setProducts(res.data)).catch(() => {});
  }, []);

  // 自动计算小计和总额
  const result = useMemo(() => {
    let subtotalSum = 0;
    const enriched = items.map((item) => {
      const qty = item.quantity || 0;
      const price = item.unitPrice || 0;
      const disc = item.discount || 0;
      const st = qty * price * (1 - disc);
      subtotalSum += st;
      return { ...item, subtotal: Math.round(st * 100) / 100 };
    });

    const taxRate = form.getFieldValue('taxRate') || 0.06;
    const total = subtotalSum * (1 + taxRate);

    return { items: enriched, subtotalSum: Math.round(subtotalSum * 100) / 100,
      total: Math.round(total * 100) / 100 };
  }, [items, form.getFieldValue('taxRate')]);

  const onProductSelect = (productId, key) => {
    const product = products.find((p) => p.id === productId);
    if (!product) return;
    setItems((prev) => prev.map((item) => {
      if (item.key !== key) return item;
      return {
        ...item,
        productId,
        name: product.name,
        spec: product.spec || '',
        unit: product.unit || '台',
        unitPrice: product.listPrice || 0,
        quantity: 1,
        discount: 0,
      };
    }));
  };

  const onItemChange = (key, field, value) => {
    setItems((prev) => prev.map((item) => {
      if (item.key !== key) return item;
      return { ...item, [field]: value };
    }));
  };

  const addRow = () => {
    const maxKey = Math.max(...items.map((i) => i.key), 0);
    setItems((prev) => [...prev, {
      key: maxKey + 1,
      productId: null,
      name: '',
      spec: '',
      quantity: 1,
      unit: '台',
      unitPrice: 0,
      discount: 0,
      subtotal: 0,
    }]);
  };

  const removeRow = (key) => {
    if (items.length <= 1) { message.warning('至少保留一个项目'); return; }
    setItems((prev) => prev.filter((i) => i.key !== key));
  };

  const handleSubmit = async (values) => {
    if (items.length === 0) { message.warning('请添加产品项目'); return; }

    const validItems = items.filter((i) => i.name);
    if (validItems.length === 0) { message.warning('请至少填写一个产品项目'); return; }

    setSubmitting(true);
    try {
      const payload = {
        customerId: values.customerId,
        type: values.type,
        taxRate: values.taxRate || 0.06,
        validUntil: values.validUntil ? values.validUntil.toISOString() : null,
        notes: values.notes || '',
        items: validItems.map((i) => ({
          productId: i.productId,
          name: i.name,
          spec: i.spec,
          quantity: i.quantity || 1,
          unit: i.unit || '台',
          unitPrice: i.unitPrice || 0,
          discount: i.discount || 0,
          subtotal: (i.quantity || 1) * (i.unitPrice || 0) * (1 - (i.discount || 0)),
        })),
      };

      const quotation = await quotationApi.create(payload);
      message.success('报价创建成功');
      navigate(`/quotations/${quotation.id}`);
    } catch (e) {
      message.error(e?.error || '创建失败');
    } finally {
      setSubmitting(false);
    }
  };

  const itemColumns = [
    {
      title: '产品', key: 'product', width: 220,
      render: (_, r) => (
        <Select
          showSearch
          placeholder="搜索选择产品"
          value={r.productId || undefined}
          onChange={(v) => onProductSelect(v, r.key)}
          filterOption={(input, option) =>
            (option.children || '').toLowerCase().includes(input.toLowerCase())
          }
          style={{ width: '100%' }}
          allowClear
        >
          {products.map((p) => (
            <Select.Option key={p.id} value={p.id}>{`${p.name} [${p.sku}]`}</Select.Option>
          ))}
        </Select>
      ),
    },
    {
      title: '规格', key: 'spec', width: 120,
      render: (_, r) => (
        <Input
          value={r.spec}
          onChange={(e) => onItemChange(r.key, 'spec', e.target.value)}
          placeholder="规格"
        />
      ),
    },
    {
      title: '数量', key: 'quantity', width: 90,
      render: (_, r) => (
        <InputNumber
          min={1} value={r.quantity}
          onChange={(v) => onItemChange(r.key, 'quantity', v || 1)}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: '单位', key: 'unit', width: 70,
      render: (_, r) => (
        <Input
          value={r.unit}
          onChange={(e) => onItemChange(r.key, 'unit', e.target.value)}
        />
      ),
    },
    {
      title: '单价(¥)', key: 'unitPrice', width: 120,
      render: (_, r) => (
        <InputNumber
          min={0} precision={2} value={r.unitPrice}
          onChange={(v) => onItemChange(r.key, 'unitPrice', v || 0)}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: '折扣', key: 'discount', width: 100,
      render: (_, r) => (
        <InputNumber
          min={0} max={1} step={0.01} precision={2} value={r.discount}
          onChange={(v) => onItemChange(r.key, 'discount', v || 0)}
          style={{ width: '100%' }}
          formatter={(v) => `${(v * 100).toFixed(0)}%`}
          parser={(v) => parseFloat(v) / 100}
        />
      ),
    },
    {
      title: '小计(¥)', key: 'subtotal', width: 120,
      render: (_, r) => {
        const idx = result.items.findIndex((i) => i.key === r.key);
        const item = idx >= 0 ? result.items[idx] : r;
        return <Text strong>{item.subtotal.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}</Text>;
      },
    },
    {
      title: '操作', key: 'actions', width: 60,
      render: (_, r) => (
        <Button size="small" danger icon={<DeleteOutlined />} onClick={() => removeRow(r.key)} />
      ),
    },
  ];

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto' }}>
      <Title level={4}>新建报价</Title>

      <Form form={form} layout="vertical" onFinish={handleSubmit}
        initialValues={{ taxRate: 0.06, type: '产品销售' }}>
        <Card size="small" title="基本信息" style={{ marginBottom: 16 }}>
          <Space style={{ width: '100%' }} size="large">
            <Form.Item name="customerId" label="客户" rules={[{ required: true, message: '请选择客户' }]}
              style={{ minWidth: 280 }}>
              <Select
                showSearch placeholder="搜索客户"
                filterOption={(input, option) =>
                  (option.children || '').toLowerCase().includes(input.toLowerCase())
                }
              >
                {customers.map((c) => (
                  <Select.Option key={c.id} value={c.id}>{c.name}</Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item name="type" label="报价类型" rules={[{ required: true }]} style={{ minWidth: 160 }}>
              <Select>
                {QUOTATION_TYPES.map((t) => (
                  <Select.Option key={t} value={t}>{t}</Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item name="validUntil" label="有效期至" style={{ minWidth: 160 }}>
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
          </Space>

          <Form.Item name="taxRate" label={
            <span>税率 <Text type="secondary">{Math.round((form.getFieldValue('taxRate') || 0.06) * 100)}%</Text></span>
          }>
            <Slider
              min={0} max={0.17} step={0.01}
              marks={{ 0: '0%', 0.06: '6%', 0.13: '13%', 0.17: '17%' }}
              tooltip={{ formatter: (v) => `${(v * 100).toFixed(0)}%` }}
            />
          </Form.Item>

          <Form.Item name="notes" label="备注">
            <Input.TextArea rows={3} placeholder="备注信息（可选）" />
          </Form.Item>
        </Card>

        {/* 产品项目表 */}
        <Card size="small" title="产品明细"
          extra={<Button size="small" icon={<PlusOutlined />} onClick={addRow}>添加项目</Button>}
          style={{ marginBottom: 16 }}>
          <Table
            columns={itemColumns}
            dataSource={items}
            rowKey="key"
            pagination={false}
            size="small"
          />

          <Divider />
          <div style={{ textAlign: 'right', fontSize: 16 }}>
            <Space direction="vertical" align="end">
              <Text>产品合计：<Text strong>{result.subtotalSum.toLocaleString('zh-CN', { style: 'currency', currency: 'CNY' })}</Text></Text>
              <Text>含税总额：<Text strong style={{ fontSize: 20, color: '#ff4d4f' }}>{result.total.toLocaleString('zh-CN', { style: 'currency', currency: 'CNY' })}</Text></Text>
            </Space>
          </div>
        </Card>

        <div style={{ textAlign: 'right' }}>
          <Space>
            <Button onClick={() => navigate('/quotations')}>取消</Button>
            <Button type="primary" htmlType="submit" loading={submitting}
              style={{ background: '#07c160', borderColor: '#07c160' }}>
              保存报价
            </Button>
          </Space>
        </div>
      </Form>
    </div>
  );
}
